import { useState } from 'react';
import { RATINGS, api, PanelHowTo } from './_barrel.js';


        // Unified subject timeline — "everything found", newest first, with
        // per-kind filter chips. Reads /api/v1/subjects/{id}/timeline (fetched
        // by the parent and passed in).
        function TimelinePanel({ timeline }) {
            const [active, setActive] = useState('all');
            const [open, setOpen] = useState(false);
            const KIND_LABEL = {
                incident: 'Incident', breach: 'Breach', sanctions: 'Sanctions',
                appointment: 'Appointed', resignation: 'Resigned',
                mention: 'Mention', report: 'Report',
            };
            const dotColour = (ev) => {
                if (ev.severity) return RATINGS.hex(ev.severity);
                switch (ev.kind) {
                    case 'breach': case 'sanctions': return 'var(--red)';
                    case 'report': return 'var(--green)';
                    case 'appointment': return 'var(--gold)';
                    case 'resignation': return 'var(--text-muted)';
                    default: return 'var(--gold)';
                }
            };
            const fmt = (s) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

            if (!timeline || !timeline.items || timeline.items.length === 0) {
                return (
                    <div className="panel mb-6">
                        <div className="panel-header flex justify-between items-center">
                            <div className="font-display text-xl gold">Timeline</div>
                            <div className="text-xs text-muted font-mono">everything found, newest first</div>
                        </div>
                        <div className="px-6 py-8 text-muted text-sm">
                            Nothing on the timeline yet — it fills in as incidents, breaches, Companies House changes, mentions and reports are found.
                        </div>
                    </div>
                );
            }

            const counts = timeline.counts || {};
            const kinds = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
            const items = active === 'all' ? timeline.items : timeline.items.filter(e => e.kind === active);
            const chip = (key, label, n) => (
                <button key={key} type="button" onClick={() => setActive(key)}
                    className="text-xs font-mono uppercase tracking-wider"
                    style={{
                        padding: '3px 11px', borderRadius: '999px', border: '1px solid',
                        borderColor: active === key ? 'var(--gold)' : 'var(--rule)',
                        color: active === key ? 'var(--gold)' : 'var(--text-muted)',
                        background: active === key ? 'var(--gold-faint, transparent)' : 'transparent',
                    }}>
                    {label} {n}
                </button>
            );

            return (
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex justify-between items-center cursor-pointer"
                        onClick={() => setOpen(!open)}>
                        <div>
                            <div className="font-display text-xl gold">Timeline</div>
                            <div className="text-xs text-muted font-mono mt-1">
                                {timeline.total} events · everything found, newest first
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {timeline.new_count > 0 && (
                                <div className="text-xs font-mono uppercase tracking-widest"
                                    style={{ color: 'var(--gold)', border: '1px solid var(--gold)', borderRadius: '999px', padding: '3px 11px' }}>
                                    {timeline.new_count} new since yesterday
                                </div>
                            )}
                            <div className="text-xs text-muted">{open ? '▾' : '▸'}</div>
                        </div>
                    </div>
                    <PanelHowTo id="timeline" />
                    {open && (
                    <div className="px-6 py-5">
                        <div className="flex flex-wrap gap-2 mb-5">
                            {chip('all', 'All', timeline.total)}
                            {kinds.map(k => chip(k, KIND_LABEL[k] || k, counts[k]))}
                        </div>
                        <div>
                            {items.map((ev, idx) => (
                                <div key={idx} className="flex gap-4 items-start py-3 border-b border-veil last:border-b-0">
                                    <div className="flex-shrink-0" style={{ width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px', background: dotColour(ev) }}></div>
                                    <div className="flex-shrink-0 text-xs font-mono text-muted" style={{ width: '96px', marginTop: '2px' }}>{fmt(ev.date)}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-paper">
                                            {ev.title}
                                            {ev.is_new && (
                                                <span className="ml-2 text-[9px] font-mono uppercase tracking-widest"
                                                    style={{ color: 'var(--gold)', border: '1px solid var(--gold)', borderRadius: '3px', padding: '1px 4px', verticalAlign: 'middle' }}>
                                                    New
                                                </span>
                                            )}
                                        </div>
                                        {ev.detail && <div className="text-xs text-muted font-mono mt-1">{ev.detail}</div>}
                                    </div>
                                    <div className="flex-shrink-0 text-[10px] font-mono uppercase tracking-widest" style={{ color: dotColour(ev), marginTop: '2px' }}>
                                        {KIND_LABEL[ev.kind] || ev.kind}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    )}
                </div>
            );
        }

export { TimelinePanel };
